import { Button } from '@mui/material';
import { useBillingClient } from 'client/client-hook';
import useAuth from 'hooks/useAuth';
import { CHECKOUT_COPY } from 'lib/billing';
import { DispatchSnackbarService } from 'services';
import { useDispatch } from 'store';
import { NotePanel } from '../NotePanel';
import { useCheckoutActions, useCheckoutState, useCheckoutStore } from '../store';
import { fetchOverdueInvoice } from '../store/overdue';
import { formatEndsOn } from './formatEndsOn';

const BANNER_COPY = CHECKOUT_COPY.banner;
const service = new DispatchSnackbarService();

export function CurrentPlanBanner() {
  const state = useCheckoutState();
  const actions = useCheckoutActions();
  const { user } = useAuth() as { user?: { tcId?: string } };
  const getBillingClient = useBillingClient();
  const dispatch = useDispatch();
  // Invoice-pay mode (overdue renewal) renders even without authCheckout, which
  // is a logged-in-subscriber flag that isn't always set for the overdue path.
  if (!state.authCheckout && !state.overdueInvoice) return null;
  const active = state.activeSubscription;
  const manageable = state.manageableSubscription;
  const pastDue = (state.subscriptions || []).find((s: any) => s?.status === 'past_due');

  if (pastDue) {
    // Settle the overdue invoice: in invoice-pay mode jump straight to Payment;
    // in plan mode (drawer opened for a purchase/modify) fetch the invoice and
    // switch the drawer into invoice-pay mode first.
    const handlePayOverdue = async () => {
      if (state.overdueInvoice) {
        actions.goToPayment();
        return;
      }
      if (!user?.tcId || !pastDue?.id) return;
      try {
        const overdue = await fetchOverdueInvoice(getBillingClient, user.tcId, pastDue.id);
        // Compat actions don't expose setOverdueInvoice — write the raw store.
        useCheckoutStore.getState().setOverdueInvoice(overdue);
        actions.goToPayment();
      } catch (err: any) {
        const msz = err?.message === 'no permission to access this RPC' ? 'Permission denied' : err?.message;
        service.dispatchSnackbar(dispatch, msz || 'Failed to load overdue invoice', 'alert', 'error', 5000);
      }
    };
    return (
      <NotePanel
        severity="error"
        title={BANNER_COPY.pastDueTitle}
        body={BANNER_COPY.pastDueBody}
        action={
          <Button variant="contained" color="error" size="small" onClick={handlePayOverdue} sx={{ borderRadius: 2 }}>
            {BANNER_COPY.pastDueCta}
          </Button>
        }
      />
    );
  }

  if (!active && manageable) {
    const when = formatEndsOn(manageable.endsOn);
    return <NotePanel severity="info" title={BANNER_COPY.scheduledCancelTitle} body={BANNER_COPY.scheduledCancelBody(when)} />;
  }

  if (!active) return null;
  const endsOn = formatEndsOn(active.endsOn);
  if (endsOn) {
    return (
      <NotePanel severity="warning" title={BANNER_COPY.cancellationScheduledTitle(endsOn)} body={BANNER_COPY.cancellationScheduledBody} />
    );
  }
  return <NotePanel severity="success" title={BANNER_COPY.activeTitle} body={BANNER_COPY.activeBody} />;
}
